import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { guardarDestino } from '@/lib/destino';
import { urlEvento } from '@/pages/assistente-evento/utils';
import CartaoMeuEvento from './components/CartaoMeuEvento';
import { ordenarEventos } from './utils';
import { preencherCompradoresFichas, preencherVagasAcampamento } from './numeros';
import type { EventoMeu } from './types';
import { useConvites } from '@/pages/convites/context';
import CartaoConvite from '@/pages/convites/components/CartaoConvite';
import CabecalhoAdmin from '@/components/feature/CabecalhoAdmin';
import Dica from '@/components/feature/Dica';
import ListaEntrada from '@/components/movimento/ListaEntrada';

export default function MeusEventosPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { convites } = useConvites();
  const { user, pronto } = useAuth();

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [eventos, setEventos] = useState<EventoMeu[]>([]);
  const [reload, setReload] = useState(0);
  const [copiado, setCopiado] = useState(false);

  const publicou = (location.state as { publicou?: boolean; slug?: string } | null) || null;
  const linkPublicado = publicou?.publicou && publicou.slug ? urlEvento(publicou.slug) : null;

  useEffect(() => {
    if (!pronto) return;
    if (!user) {
      guardarDestino();
      navigate(`/entrar?voltar=${encodeURIComponent('/meus-eventos')}`, { replace: true });
      return;
    }
    let ativo = true;

    async function carregar() {
      setLoading(true);
      setErro(null);
      try {
        // IDs dos eventos que a pessoa administra (dona ou equipe ativa).
        const { data: idsData, error: errIds } = await supabase.rpc('meus_eventos');
        if (errIds) throw errIds;
        const ids = ((idsData as unknown[]) || [])
          .map((n) => Number(n))
          .filter((n) => Number.isFinite(n) && n > 0);

        if (ids.length === 0) {
          if (ativo) setEventos([]);
          return;
        }

        const { data: eventosData, error: errEventos } = await supabase
          .from('eventos')
          .select(
            'id, nome, descricao, imagem_desktop_url, imagem_mobile_url, cidade, uf, inicio_em, fim_em, status, vende_ingressos, usa_equipes, so_divulgacao, entrada_franca',
          )
          .in('id', ids);
        if (errEventos) throw errEventos;

        const linhas = (eventosData as EventoMeu[]) || [];

        // Números: inscritos vêm de vw_painel_evento.
        const filtroRifa = `evento_id.in.(${ids.join(',')}),evento_beneficiado_id.in.(${ids.join(',')})`;
        const [resPainel, resRifa] = await Promise.all([
          supabase.from('vw_painel_evento').select('evento_id, ativas').in('evento_id', ids),
          supabase
            .from('vw_rifa_publica')
            .select('evento_id, evento_beneficiado_id, status, premios')
            .or(filtroRifa),
        ]);
        if (resPainel.error) throw resPainel.error;
        if (resRifa.error) throw resRifa.error;

        const inscritosMap = new Map<number, number>();
        ((resPainel.data as { evento_id: number; ativas: number }[]) || []).forEach((r) => {
          inscritosMap.set(
            r.evento_id,
            (inscritosMap.get(r.evento_id) ?? 0) + (Number(r.ativas) || 0),
          );
        });

        const semFotoMap = new Map<number, number>();
        (
          (resRifa.data as {
            evento_id: number;
            evento_beneficiado_id: number | null;
            status: string;
            premios: { imagem_url: string | null }[] | null;
          }[]) || []
        ).forEach((r) => {
          if (r.status !== 'publicada' || !r.premios) return;
          const semFoto = r.premios.filter((p) => !p.imagem_url).length;
          if (semFoto > 0) {
            semFotoMap.set(r.evento_id, semFoto);
            if (r.evento_beneficiado_id) semFotoMap.set(r.evento_beneficiado_id, semFoto);
          }
        });

        const lista: EventoMeu[] = linhas.map((e) => ({
          ...e,
          inscritos: inscritosMap.get(e.id) ?? 0,
          premiosSemFoto: semFotoMap.get(e.id) ?? 0,
          compradoresFichas: null as number | null,
          vagas: null as EventoMeu['vagas'],
        }));

        // Fichas (entrada franca) e acampamento têm contadores próprios:
        // nunca "0 ingressos vendidos" e nunca equipe somada com campista.
        await Promise.all([
          preencherCompradoresFichas(lista),
          preencherVagasAcampamento(lista),
        ]);

        if (ativo) setEventos(ordenarEventos(lista));
      } catch (e) {
        if (ativo) {
          setErro(
            mensagemAmigavel(e, 'Não foi possível carregar seus eventos. Tente novamente.'),
          );
        }
      } finally {
        if (ativo) setLoading(false);
      }
    }

    carregar();
    return () => {
      ativo = false;
    };
  }, [navigate, reload, pronto, user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background-100">
        <div className="mx-auto w-full max-w-[1120px] px-4 py-8">
          <div className="h-8 w-48 esqueleto rounded bg-secondary-100" />
          <div className="mt-2 h-4 w-72 esqueleto rounded bg-secondary-100" />
          <div className="mt-6 space-y-3">
            <div className="h-28 w-full esqueleto rounded-lg bg-secondary-100" />
            <div className="h-28 w-full esqueleto rounded-lg bg-secondary-100" />
            <div className="h-28 w-full esqueleto rounded-lg bg-secondary-100" />
          </div>
        </div>
      </div>
    );
  }

  if (erro) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-4 py-8">
        <div className="w-full max-w-[420px] overflow-hidden rounded-lg border border-secondary-200 bg-white">
          <div className="h-[10px] w-full bg-primary-500" />
          <div className="p-6 text-center">
            <h1 className="text-xl font-normal text-foreground-950">Não foi possível carregar</h1>
            <p className="mt-2 text-sm text-secondary-500">{erro}</p>
            <button
              type="button"
              onClick={() => setReload((r) => r + 1)}
              className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-600"
            >
              Tentar de novo
            </button>
          </div>
        </div>
      </div>
    );
  }

  async function copiarLink() {
    if (!linkPublicado) return;
    try {
      await navigator.clipboard.writeText(linkPublicado);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      /* ignora */
    }
  }

  return (
    <div className="min-h-screen bg-background-100">
      <div className="mx-auto w-full max-w-[1120px] px-4 py-8">
        <CabecalhoAdmin
          eyebrow="Área do organizador"
          titulo="Meus eventos"
          aoLadoDoTitulo={<Dica id="meus-eventos" />}
          subtitulo="Crie novos eventos e gerencie os que você já organiza."
          acao={
            <Link
              to="/criar"
              className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600 sm:w-auto"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-add-line" />
              </span>
              Novo evento
            </Link>
          }
        />

        {linkPublicado && (
          <div className="mt-6 rounded-lg border border-green-200 bg-green-50 px-4 py-4">
            <div className="flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-500 text-white">
                <i className="ri-check-line" />
              </span>
              <p className="text-[15px] font-medium text-green-800">Evento publicado!</p>
            </div>
            <div className="mt-3 flex items-center justify-between gap-2 rounded-md bg-white px-3 py-2">
              <code className="min-w-0 truncate font-mono text-xs text-foreground-800">
                {linkPublicado}
              </code>
              <button
                type="button"
                onClick={copiarLink}
                className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
              >
                <i className={copiado ? 'ri-check-line' : 'ri-file-copy-line'} />
                {copiado ? 'Copiado' : 'Copiar'}
              </button>
            </div>
          </div>
        )}

        {convites.length > 0 && (
          <div className="mt-6 space-y-3">
            {convites.map((c) => (
              <CartaoConvite key={c.convite_id} convite={c} />
            ))}
          </div>
        )}

        {eventos.length === 0 ? (
          <div className="mt-6 overflow-hidden rounded-2xl border border-secondary-200 bg-white">
            <div className="h-[10px] w-full bg-primary-500" />
            <div className="flex flex-col items-center px-6 py-14 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                <i className="ri-megaphone-line text-3xl" />
              </span>
              <h2 className="mt-5 text-xl font-semibold text-foreground-950">
                Você ainda não organiza nenhum evento
              </h2>
              <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-600">
                Cadastre seu primeiro evento agora — a gente te guia passo a passo, do nome às
                inscrições, sem complicação.
              </p>
              <Link
                to="/criar"
                className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-7 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-add-line" />
                </span>
                Cadastrar meu primeiro evento
              </Link>
            </div>
          </div>
        ) : (
          <>
            <ListaEntrada
              itens={eventos}
              chave={(evento) => evento.id}
              className="mt-6 space-y-5"
            >
              {(evento) => <CartaoMeuEvento evento={evento} />}
            </ListaEntrada>

            <div className="mt-8 flex justify-center">
              <Link
                to="/criar"
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border border-primary-300 px-6 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-100"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-add-line" />
                </span>
                Novo evento
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}